import {
  CLIENT_FORM_ALREADY_SENT_CODE, CLIENT_RECORD_NOT_READY_CODE, CLIENT_RECORD_STALE_SAVE_CODE,
} from '@steward-app/steward-types';
import { ApiError, createApiClient } from './client';

/**
 * The client's onboarding form on the unified client record (docs/43 slice 4c,
 * crossmarc/steward-app#2699). Three façade routes, addressed by the firm's slug (the firm
 * workspace and the client are the server's to resolve from the login):
 *
 *   GET  /intake/{firmSlug}/client-record/onboarding           the client's answers at this firm
 *   POST /intake/{firmSlug}/client-record/onboarding/answers   the whole form, versioned
 *   POST /intake/{firmSlug}/client-record/onboarding/send      send it, once
 *
 * What stays on `onboardingForm.ts`'s `/me/onboarding-forms/{firmWorkspaceId}` routes: the
 * firm's name and asked sections, the information requests and their answers, full account
 * numbers, and the tax id. A `403 STEP_UP_REQUIRED` from any call here is handled by the API
 * client's interceptor, as it is there.
 */

export interface OnboardingRecordListItem { id: string; fields: Record<string, unknown> }

export interface OnboardingRecordView {
  form: 'onboarding';
  formClosed: boolean;
  formClosedBy?: 'firm' | 'setup';
  answers: {
    _v: number;
    /** Each `{ value, via, by?, at }`; `via` is the form or writer that last changed the value. */
    answers: Record<string, unknown>;
    lists: Record<string, OnboardingRecordListItem[]>;
  };
  /** When the client sent the form; absent until they do. */
  onboardingSentAt?: string;
  /** When the client agreed to staff entry (#2538): the date the advisor notice names. */
  staffEntryConsentAt?: string;
}

export interface OnboardingRecordSaveBody {
  expectedVersion: number;
  answers: Record<string, string | boolean | string[]>;
  lists: Record<string, Array<{ id: string; fields: Record<string, string | boolean | string[]> }>>;
}

const path = (firmSlug: string): string => `/intake/${encodeURIComponent(firmSlug)}/client-record/onboarding`;

export const onboardingRecordQueryKey = (firmSlug: string) => ['onboarding-record', firmSlug] as const;

export function getOnboardingRecord(token: string, firmSlug: string): Promise<OnboardingRecordView> {
  return createApiClient(token).get<OnboardingRecordView>(path(firmSlug));
}

export function saveOnboardingRecordAnswers(
  token: string, firmSlug: string, body: OnboardingRecordSaveBody,
): Promise<{ version: number }> {
  return createApiClient(token).post<{ version: number }>(`${path(firmSlug)}/answers`, body);
}

export function sendOnboardingRecord(token: string, firmSlug: string): Promise<{ form: 'onboarding'; sentAt: string }> {
  return createApiClient(token).post<{ form: 'onboarding'; sentAt: string }>(`${path(firmSlug)}/send`, {});
}

/** The record is still being prepared by the intake writer: poll through it. */
export const isRecordNotReady = (err: unknown): boolean => err instanceof ApiError && err.code === CLIENT_RECORD_NOT_READY_CODE;
/** The save carried a version the record has moved past: merge and send again. */
export const isStaleSave = (err: unknown): boolean => err instanceof ApiError && err.status === 409 && err.code === CLIENT_RECORD_STALE_SAVE_CODE;
/** The form was already sent: a second send is the server's no-op, which the form reads as success. */
export const isAlreadySent = (err: unknown): boolean => err instanceof ApiError && err.status === 409 && err.code === CLIENT_FORM_ALREADY_SENT_CODE;
